<template>
  <div class="admin-page">
    <div class="admin-card">
      <img :src="logo" alt="BORAsuite Logo" class="login-logo" />

      <h1 class="title">
        <span v-if="phase === 'CREDENTIALS'">Sign In</span>
        <span v-else>Enter OTP</span>
      </h1>

      <p class="subtitle" v-if="phase === 'CREDENTIALS'">
        Sign in to manage clients, applications, billing, and platform operations.
      </p>
      <p class="subtitle" v-else>
        We sent a 6-digit code to <strong>{{ email }}</strong>.
      </p>

      <p v-if="infoMessage" class="info-text">{{ infoMessage }}</p>
      <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>

      <form @submit.prevent="onSubmit" class="form">
        <template v-if="phase === 'CREDENTIALS'">
          <div class="form-group">
            <input
              type="email"
              v-model="email"
              required
              autocomplete="email"
              placeholder="Enter Work Email"
            />
          </div>

          <div class="form-group password-field">
            <input
              :type="showPassword ? 'text' : 'password'"
              v-model="password"
              required
              autocomplete="current-password"
              placeholder="Enter Password"
            />
            <button
              type="button"
              class="eye-btn"
              @click="showPassword = !showPassword"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
            >
              <i :class="showPassword ? 'fa-regular fa-eye-slash' : 'fa-regular fa-eye'"></i>
            </button>
          </div>

          <div class="links-row">
            <button type="button" class="link-btn" @click="onForgotPassword">
              Forgot Password?
            </button>
            <span class="separator"></span>
            <button type="button" class="link-btn" @click="onCreateAccount">
              Need access? <span class="link-strong">Contact Admin Team</span>
            </button>
          </div>
        </template>

        <template v-else>
          <div class="form-group">
            <input
              type="text"
              v-model="otpCode"
              required
              maxlength="6"
              placeholder="Enter 6-digit OTP"
            />
          </div>

          <div class="links-row">
            <button type="button" class="link-btn" @click="backToCredentials">
              Back to Email & Password
            </button>
          </div>
        </template>

        <button class="btn-signin" type="submit" :disabled="isSubmitting">
          <span v-if="phase === 'CREDENTIALS'">
            <span v-if="isSubmitting">Verifying credentials...</span>
            <span v-else>Continue</span>
          </span>
          <span v-else>
            <span v-if="isSubmitting">Verifying OTP...</span>
            <span v-else>Verify & Sign In</span>
          </span>
        </button>
      </form>
    </div>

    <p class="copyright">
      &copy; {{ currentYear }} BORAsuite. All rights reserved.
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../../stores/auth'
import { useUiStore } from '../../stores/ui'
import logo from '../../assets/images/Logo-BORAsuite-1.png'
import { isAllowedStaffEmail } from '../../constants/staffDomains'
import {
  ADMIN_AUTH_APPLICATION_CODE,
  formatAdminAuthError,
  startAdminLogin,
} from '../../services/adminAuth'

type Phase = 'CREDENTIALS' | 'OTP'

const router = useRouter()
const route = useRoute()
const auth = useAuthStore()
const ui = useUiStore()

const phase = ref<Phase>('CREDENTIALS')
const email = ref('')
const password = ref('')
const otpCode = ref('')
const showPassword = ref(false)
const localError = ref('')
const infoMessage = ref('')
const isSubmitting = ref(false)
const errorMessage = computed(() => localError.value || auth.error)
const currentYear = new Date().getFullYear()

function resolvePostLoginTarget() {
  const redirect = String(route.query.redirect || '').trim()
  if (redirect.startsWith('/') && !redirect.startsWith('//')) {
    return redirect
  }
  return '/admin/dashboard'
}

const onSubmit = async () => {
  localError.value = ''
  auth.error = null
  infoMessage.value = ''
  isSubmitting.value = true

  try {
    if (phase.value === 'CREDENTIALS') {
      if (!isAllowedStaffEmail(email.value)) {
        localError.value = `Access is restricted to LWEGATECH Team only.`
        return
      }

      const data = await startAdminLogin({
        email: email.value,
        password: password.value,
      })

      if (data.otp_required) {
        phase.value = 'OTP'
        password.value = ''
        infoMessage.value = data.detail || 'OTP has been sent to your email.'
        ui.showToast(infoMessage.value, 'warning')
      } else {
        infoMessage.value = data.detail || 'Login completed.'
      }
      return
    }

    await auth.loginAdminWithOtp({
      email: email.value,
      otp_code: otpCode.value,
      application_code: ADMIN_AUTH_APPLICATION_CODE,
    })

    if (!auth.isAdmin) {
      localError.value = 'You are not authorized to access the admin panel.'
      auth.logout()
      return
    }

    ui.showToast(`Welcome back, ${auth.user?.full_name || 'Admin'}!`, 'success')
    router.push(resolvePostLoginTarget())
  } catch (e: any) {
    console.error(e)
    if (!localError.value && !auth.error) {
      localError.value = formatAdminAuthError(e, 'Something went wrong. Please try again.')
    }
  } finally {
    isSubmitting.value = false
  }
}

const onForgotPassword = () => {
  router.push({
    name: 'ForgotPassword',
    query: { mode: 'admin' },
  })
}

const onCreateAccount = () => {
  ui.showToast(
    `Admin onboarding is restricted to LWEGATECH Staff`,
    'warning',
  )
}

const backToCredentials = () => {
  phase.value = 'CREDENTIALS'
  otpCode.value = ''
  infoMessage.value = ''
  localError.value = ''
  auth.error = null
}
</script>
